import React from 'react';
import { Calculator, Users, FileText, TrendingUp, Shield, Clock } from 'lucide-react';

export const ServicesSection = () => {
  const services = [
    {
      icon: Calculator,
      title: 'Complete Finance Management',
      description: 'Full ownership of your financial operations—from bookkeeping to financial statements. We handle it all.',
      features: ['Daily bookkeeping', 'Month-end closing', 'Financial statements', 'Budget planning']
    },
    {
      icon: FileText,
      title: 'Accounts & Compliance',
      description: 'Stay 100% compliant with all regulations. We ensure you\'re always audit-ready and stress-free.',
      features: ['GST filing', 'TDS returns', 'Income tax compliance', 'Statutory audits']
    },
    {
      icon: Users,
      title: 'Payroll Outsourcing',
      description: 'Complete payroll management so your team gets paid on time, every time, with zero hassle.',
      features: ['Salary processing', 'PF & ESI compliance', 'Payslip generation', 'Employee tax planning']
    },
    {
      icon: TrendingUp,
      title: 'Cash Flow Management',
      description: 'Real-time visibility into your cash position. Make informed decisions with confidence.',
      features: ['Cash flow forecasting', 'Working capital optimization', 'Expense tracking', 'Financial dashboards']
    },
    {
      icon: Shield,
      title: 'Due Diligence Ready',
      description: 'Always investor-ready. Clean books, proper documentation, and transparent reporting.',
      features: ['Audit preparation', 'Investor reporting', 'Data room management', 'Compliance certificates']
    },
    {
      icon: Clock,
      title: 'Dedicated Support',
      description: 'Your dedicated CA team is just a call away. Quick responses, proactive solutions.',
      features: ['Dedicated account manager', '24-hour response time', 'Monthly review calls', 'Strategic advisory']
    }
  ];

  return (
    <section id="services" className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-gray-50 to-white">
      <div className="max-w-7xl mx-auto">
        {/* Section Header */}
        <div className="text-center mb-16">
          <span className="inline-block px-4 py-2 rounded-full bg-emerald-100 text-emerald-700 text-sm font-medium mb-4">
            Our Services
          </span>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            One-Stop Solution for All Your
            <span className="block bg-gradient-to-r from-emerald-600 to-teal-600 bg-clip-text text-transparent">
              Finance & Accounting Needs
            </span>
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            From daily bookkeeping to strategic advisory—we've got you completely covered.
          </p>
        </div>

        {/* Services Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {services.map((service, index) => {
            const Icon = service.icon;
            return (
              <div
                key={service.title || `svc-${index}`}
                className="group bg-white rounded-2xl p-8 border border-gray-200 hover:border-emerald-300 hover:shadow-2xl transition-all duration-300 hover:-translate-y-1"
              >
                <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-emerald-100 to-teal-100 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                  <Icon className="w-8 h-8 text-emerald-600" />
                </div>
                
                <h3 className="text-2xl font-bold text-gray-900 mb-3">{service.title}</h3>
                <p className="text-gray-600 mb-6 leading-relaxed">{service.description}</p>
                
                <ul className="space-y-2">
                  {service.features.map((feature, idx) => (
                    <li key={`feature-${idx}`} className="flex items-start text-gray-700">
                      <div className="w-1.5 h-1.5 rounded-full bg-emerald-600 mt-2 mr-3 flex-shrink-0"></div>
                      <span className="text-sm">{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>

        {/* Bottom CTA */}
        <div className="mt-16 text-center">
          <p className="text-lg text-gray-600 mb-6">
            Not sure what you need? Let's talk and create a custom plan for your business.
          </p>
        </div>
      </div>
    </section>
  );
};